import React, { useState } from "react";
import {
  Flame, Brain, Dumbbell, Trophy, Crown, Sparkles, Scale,
  Lock, CheckCircle2, Snowflake, Share2
} from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { shareToSocial } from "../utils/share";

export const getIcon = (iconName, className = "") => {
  const icons = {
    flame: <Flame className={className} strokeWidth={1.5} />,
    brain: <Brain className={className} strokeWidth={1.5} />,
    dumbbell: <Dumbbell className={className} strokeWidth={1.5} />,
    trophy: <Trophy className={className} strokeWidth={1.5} />,
    crown: <Crown className={className} strokeWidth={1.5} />,
    sparkles: <Sparkles className={className} strokeWidth={1.5} />,
    scale: <Scale className={className} strokeWidth={1.5} />
  };
  return icons[iconName] || <Trophy className={className} strokeWidth={1.5} />;
};

export const getTierColors = (tier, unlocked) => {
  if (!unlocked) return "bg-slate-100 text-slate-400 border-slate-200";
  const colors = {
    bronze: "bg-gradient-to-br from-amber-600 to-amber-800 text-white border-amber-500",
    silver: "bg-gradient-to-br from-slate-300 to-slate-500 text-white border-slate-400",
    gold: "bg-gradient-to-br from-yellow-400 to-amber-500 text-white border-yellow-400",
    platinum: "bg-gradient-to-br from-sky-300 to-indigo-400 text-white border-sky-400"
  };
  return colors[tier] || colors.bronze;
};

export const getTierBadgeColors = (tier) => {
  const colors = {
    bronze: "bg-amber-100 text-amber-700",
    silver: "bg-slate-100 text-slate-600",
    gold: "bg-yellow-100 text-yellow-700",
    platinum: "bg-sky-100 text-sky-700"
  };
  return colors[tier] || colors.bronze;
};

export const AchievementCard = ({ achievement, shareAchievement, t }) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareText = `${achievement.name} - ${achievement.description} | LumiFit`;

  return (
    <div
      className={`glass-card p-5 transition-all duration-300 ${achievement.unlocked ? 'glass-card-hover' : 'opacity-70'}`}
      data-testid={`achievement-${achievement.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border-2 ${getTierColors(achievement.tier, achievement.unlocked)}`}>
          {achievement.unlocked 
            ? getIcon(achievement.icon, "w-7 h-7")
            : <Lock className="w-6 h-6" strokeWidth={1.5} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-medium truncate ${achievement.unlocked ? 'text-sky-800' : 'text-slate-500'}`}>
              {achievement.name}
            </h3>
            {achievement.unlocked && (
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-slate-500 mb-2">{achievement.description}</p>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className={getTierBadgeColors(achievement.tier) + " px-2 py-0.5 rounded-full capitalize"}>
                {achievement.tier}
              </span>
              <span className="text-slate-500">
                {achievement.progress}/{achievement.requirement}
              </span>
            </div>
            <Progress 
              value={(achievement.progress / achievement.requirement) * 100} 
              className="h-1.5 bg-slate-100" 
            />
          </div>
          {achievement.unlocked && achievement.unlocked_at && (
            <div className="text-xs text-green-600 mt-2 flex items-center justify-between">
              <span>{t('achievements.unlockedOn')} {new Date(achievement.unlocked_at).toLocaleDateString()}</span>
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowShareMenu(!showShareMenu); }}
                  className="text-sky-500 hover:text-sky-700 p-1"
                  data-testid={`share-achievement-${achievement.id}`}
                >
                  <Share2 className="w-3.5 h-3.5" />
                </button>
                {showShareMenu && (
                  <div className="absolute right-0 bottom-full mb-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1 z-30 min-w-[120px]" data-testid={`share-menu-${achievement.id}`}>
                    <button onClick={(e) => { e.stopPropagation(); shareToSocial("twitter", shareText); setShowShareMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-sky-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center gap-2"
                      data-testid={`share-twitter-${achievement.id}`}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      X (Twitter)
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); shareToSocial("facebook", shareText); setShowShareMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-sky-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center gap-2"
                      data-testid={`share-facebook-${achievement.id}`}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      Facebook
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); shareToSocial("instagram", shareText); setShowShareMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-sky-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center gap-2"
                      data-testid={`share-instagram-${achievement.id}`}
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                      Instagram
                    </button>
                    <div className="border-t border-slate-200 dark:border-slate-700 my-0.5" />
                    <button onClick={(e) => { e.stopPropagation(); shareAchievement(achievement, null, t); setShowShareMenu(false); }}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-sky-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 flex items-center gap-2"
                      data-testid={`share-copy-${achievement.id}`}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      {t('share.copyLink') || 'Copy Link'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const StreakBanner = ({ streak, shareStreak, onUseFreeze, t }) => (
  <div className="glass-card p-6 mb-8 glow-primary" data-testid="streak-banner">
    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shadow-lg">
          <Flame className="w-8 h-8 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-sm text-slate-500">{t('achievements.currentStreak')}</p>
          <p className="text-4xl font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: 'Outfit' }}>
            {streak?.current_streak || 0} <span className="text-lg text-sky-400">{t('common.days')}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-6">
        <div className="text-center">
          <p className="text-2xl font-light text-sky-700 dark:text-sky-300" style={{ fontFamily: 'Outfit' }}>{streak?.longest_streak || 0}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('achievements.longest')}</p>
        </div>
        <div className="w-px h-10 bg-sky-200 dark:bg-slate-600" />
        <div className="text-center">
          <p className="text-2xl font-light text-cyan-600 dark:text-cyan-400" style={{ fontFamily: 'Outfit' }}>{streak?.total_physical || 0}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('achievements.physical')}</p>
        </div>
        <div className="w-px h-10 bg-sky-200 dark:bg-slate-600" />
        <div className="text-center">
          <p className="text-2xl font-light text-indigo-600 dark:text-indigo-400" style={{ fontFamily: 'Outfit' }}>{streak?.total_cognitive || 0}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('achievements.cognitive')}</p>
        </div>
      </div>
      {/* Freeze Streak Section */}
      <div className="flex items-center gap-3" data-testid="freeze-streak-section">
        <div className="relative">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            (streak?.freeze_tokens || 0) > 0
              ? 'bg-gradient-to-r from-sky-100 to-cyan-100 dark:from-sky-900/40 dark:to-cyan-900/40 border border-sky-200 dark:border-sky-700'
              : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
          }`}>
            <Snowflake className={`w-5 h-5 ${(streak?.freeze_tokens || 0) > 0 ? 'text-sky-500 animate-pulse' : 'text-slate-300 dark:text-slate-500'}`} />
            <div className="text-center">
              <p className={`text-lg font-semibold leading-none ${(streak?.freeze_tokens || 0) > 0 ? 'text-sky-700 dark:text-sky-300' : 'text-slate-400'}`} style={{ fontFamily: 'Outfit' }}>
                {streak?.freeze_tokens || 0}
              </p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">
                {t('achievements.freezeTokens') || 'Freezes'}
              </p>
            </div>
          </div>
        </div>
        {onUseFreeze && (streak?.freeze_tokens || 0) > 0 && (
          <Button
            onClick={onUseFreeze}
            size="sm"
            className="bg-gradient-to-r from-sky-400 to-cyan-400 text-white hover:from-sky-500 hover:to-cyan-500 rounded-full shadow-md"
            data-testid="use-freeze-btn"
          >
            <Snowflake className="w-4 h-4 mr-1" />
            {t('achievements.useFreeze') || 'Use Freeze'}
          </Button>
        )}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => shareStreak(streak, t)}
        className="text-sky-500 hover:text-sky-700"
        data-testid="share-streak-btn"
      >
        <Share2 className="w-5 h-5" strokeWidth={1.5} />
      </Button>
    </div>
    {/* Freeze info tooltip */}
    <p className="text-xs text-center text-slate-400 dark:text-slate-500 mt-3">
      {t('achievements.freezeHint') || 'Earn 1 freeze for every 7-day streak. Use it to protect your streak when you miss a day.'}
    </p>
  </div>
);
